import { css } from "@emotion/css";
import { CoffeeSpiltIllustration, QuestionIllustration } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import type { GitRef, ProjectResource, ValidateGitRefV2Response } from "@octopusdeploy/octopus-server-client";
import { ValidateGitRefV2ResponseType } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import type React from "react";
import { BranchSelector } from "~/areas/projects/components/BranchSelector/BranchSelector";
import InternalLink from "~/components/Navigation/InternalLink";
interface VcsErrorProps {
    project: ProjectResource;
    error: ValidateGitRefV2Response;
    gitRef: GitRef | undefined;
    setGitRef: (gitRef: GitRef) => void;
}
export function VcsError({ project, error, gitRef, setGitRef }: VcsErrorProps) {
    const branchSelectorErrorMessage = getBranchSelectorErrorMessage(error);
    const branchSelector = <BranchSelector project={project} gitRef={gitRef} setGitRef={setGitRef} errorMessage={branchSelectorErrorMessage}/>;
    const errorInfoText = getErrorInfoText(error, project.Slug, project.SpaceId);
    const errorTitle = getErrorTitle(error);
    return <VcsErrorInternal errorTitle={errorTitle} error={error} branchSelector={branchSelector} errorInfoText={errorInfoText}/>;
}
interface VcsErrorInternalProps {
    error: ValidateGitRefV2Response;
    branchSelector: React.ReactNode;
    errorInfoText: React.ReactElement;
    errorTitle: string;
}
export function VcsErrorInternal({ error, branchSelector, errorInfoText, errorTitle }: VcsErrorInternalProps) {
    const errorIllustration = getErrorIllustration(error);
    const errorMessage = getErrorMessage(error);
    const showBranchSelector = error.Type !== ValidateGitRefV2ResponseType.ConnectionFailed;
    return (<div className={containerStyles}>
            {errorIllustration}
            <h2 className={headerStyles}>{errorTitle}</h2>
            <p className={paragraphStyles}>
                {errorMessage}
                <br />
                {errorInfoText}
            </p>
            {showBranchSelector && <div>{branchSelector}</div>}
        </div>);
}
function getErrorIllustration(error: ValidateGitRefV2Response) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.ConnectionFailed:
            return <QuestionIllustration />;
        default:
            return <CoffeeSpiltIllustration />;
    }
}
function getErrorTitle(error: ValidateGitRefV2Response): string {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.GitRefNotFound:
            return "Could not load the selected git ref";
        default:
            return "Could not access version control";
    }
}
function getErrorMessage(error: ValidateGitRefV2Response) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.ConnectionFailed:
            return "We're unable to establish a connection to the git instance.";
        default:
            return error.Message;
    }
}
export function getBranchSelectorErrorMessage(error: ValidateGitRefV2Response) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.GitRefNotFound:
            return "unknown git ref";
        case ValidateGitRefV2ResponseType.OclNotFound:
            return "ocl not found";
        default:
            return undefined;
    }
}
function getErrorInfoText(error: ValidateGitRefV2Response, projectSlug: string, spaceId: string) {
    switch (error.Type) {
        case ValidateGitRefV2ResponseType.ConnectionFailed:
            return (<>
                    This could be due to an outage or check your <InternalLink to={links.projectVersionControlSettingsPage.generateUrl({ spaceId, projectSlug })}>Version Control Settings</InternalLink>.
                </>);
        default:
            return (<>
                    <span>Try selecting a different branch below</span>.
                </>);
    }
}
const containerStyles = css({
    display: "flex",
    flexDirection: "column",
    padding: `${space[32]} ${space[32]} ${space[80]}`,
    gap: space[16],
    alignItems: "center",
});
const headerStyles = css({
    margin: 0,
    font: text.heading.large,
});
const paragraphStyles = css({
    margin: 0,
    font: text.body.regular.large,
    textAlign: "center",
});
